import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  onMessage,
} from "firebase/messaging";
import { firebaseConfig } from "./firebase.config";
import { post } from "src/services/api";

export enum NotificationTypes {
  DEFAULT_NOTIFICATION = "defaultNotification",
  FOLLOW = "follow",
  NEW_POST = "newPost",
  NEW_REEL = "newReel",
  NEW_AUDITION = "newAudition",
  NEW_OPENCALL = "newOpenCall",
  CLAP_ON_POST = "clapOnPost",
  CLAP_ON_REEL = "clapOnReel",
  CLAP_ON_AUDITION = "clapOnAudition",
  COMMENT_ON_POST = "commentOnPost",
  COMMENT_ON_REEL = "commentOnReel",
  COMMENT_ON_AUDITION = "commentOnAudition",
  REPLY_COMMENT = "replyComment",
  GO_LIVE = "goLive",
}

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize messaging as null
export let messaging: Messaging | null = null;

// Initialize messaging if supported
export const initMessaging = async (): Promise<boolean> => {
  try {
    const supported = await isSupported();
    if (supported) {
      messaging = getMessaging(app);

      // Listen for foreground messages
      onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);

        // Show browser notification for foreground messages
        if ("Notification" in window && Notification.permission === "granted") {
          const notificationTitle =
            payload?.data?.title ||
            payload?.notification?.title ||
            "New Notification";
          const notificationBody =
            payload?.data?.body ||
            payload?.notification?.body ||
            "You have a new notification";

          new Notification(notificationTitle, {
            body: notificationBody,
            icon: "/logo/Logo.png",
            badge: "/logo/Logo.png",
          });
        }
      });

      return true;
    }
  } catch (error) {
    console.log("Firebase messaging not supported:", error);
  }
  return false;
};

// Request notification permission
export const requestPermission = async (): Promise<boolean> => {
  try {
    // Check if notifications are supported
    if (!("Notification" in window)) {
      return false;
    }

    // If already granted, generate token and return
    if (Notification.permission === "granted") {
      await generateToken();
      return true;
    }

    // If previously denied, don't prompt again
    if (Notification.permission === "denied") {
      return false;
    }

    // Only request if permission is "default"
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      await generateToken();
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return false;
  }
};

// Generate and register FCM token
const generateToken = async (): Promise<string | null> => {
  try {
    // Initialize messaging if not done already
    if (!messaging) {
      const initialized = await initMessaging();
      if (!initialized) {
        console.log("Failed to initialize messaging");
        return null;
      }
    }

    if (!messaging) {
      console.log("Messaging not initialized");
      return null;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.log("VAPID key not found in environment variables");
      return null;
    }

    const currentToken = await getToken(messaging, {
      vapidKey: vapidKey,
    });

    if (currentToken) {
      // Get or create device ID
      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId = "web" + Math.random().toString(20).substring(2, 14);
        localStorage.setItem("deviceId", deviceId);
      }

      // Register token with backend
      const token = localStorage.getItem("token");
      if (token) {
        await post(
          `${process.env.REACT_APP_API_URL}/device-tokens`,
          {
            deviceId,
            deviceType: "Web",
            token: currentToken,
          },
          {
            Authorization: `Bearer ${token}`,
          },
        );
      }

      return currentToken;
    } else {
      console.log("No registration token available");
    }
  } catch (err) {
    console.log("Error generating token:", err);
  }
  return null;
};

// Initialize notifications on app start - simplified
export const initializeNotifications = async (): Promise<void> => {
  try {
    await initMessaging();

    // Only auto-generate token if permission is already granted
    // Don't auto-request permission
    if ("Notification" in window && Notification.permission === "granted") {
      await generateToken();
    }
  } catch (error) {
    console.log("Error initializing notifications:", error);
  }
};
